// Registration API handlers for Food Guard forms
class RegistrationAPI {
    constructor() {
        this.apiService = new ApiService();
    }

    // Restaurant Registration
    async submitRestaurantForm(data) {
        try {
            const response = await this.apiService.post('/register/restaurant', data);
            this.showSuccessMessage('toaster.success.restaurant_registration');
            return response;
        } catch (error) {
            this.showErrorMessage('toaster.error.restaurant_registration');
            throw error;
        }
    }

    async loadRestaurantDropdowns() {
        try {
            const formData = await this.apiService.get('/register/form-data');
            
            // Type of Restaurant dropdown
            if (formData.data?.restaurant_types) {
                this.populateDropdown('type-of-restaurant', formData.data.restaurant_types);
            } else {
                console.warn('No restaurant_types data found');
            }
            
            // Category dropdown
            if (formData.data?.categories) {
                this.populateDropdown('category-id', formData.data.categories);
            } else {
                console.warn('No categories data found');
            }
            
            // Services Offered dropdown
            if (formData.data?.services_offered) {
                this.populateDropdown('services-offered', formData.data.services_offered);
            } else {
                console.warn('No services_offered data found');
            }
        } catch (error) {
            console.error('Failed to load restaurant dropdowns:', error);
        }
    }

    // Vendor Registration
    async submitVendorForm(data) {
        try {
            const response = await this.apiService.post('/register/supplier', data);
            this.showSuccessMessage('toaster.success.vendor_registration');
            return response;
        } catch (error) {
            this.showErrorMessage('toaster.error.vendor_registration');
            throw error;
        }
    }

    async loadVendorDropdowns() {
        try {
            const formData = await this.apiService.get('/register/form-data');
            
                if (formData.vendor.product_categories) {
                    this.populateDropdown('vendor-category', formData.vendor.product_categories);
                }
                if (formData.vendor.regions) {
                    this.populateDropdown('vendor-region', formData.vendor.regions);
                }
                if (formData.vendor.supplier_types) {
                    this.populateDropdown('supplier-type', formData.vendor.supplier_types);
                }
        } catch (error) {
            console.error('Failed to load vendor dropdowns:', error);
        }
    }

    // Delivery Registration
    async submitDeliverForm(data) {
        try {
            const response = await this.apiService.post('/register/delivery', data);
            this.showSuccessMessage('toaster.success.delivery_registration');
            return response;
        } catch (error) {
            this.showErrorMessage('toaster.error.delivery_registration');
            throw error;
        }
    }

    async loadDeliverDropdowns() {
        try {
            const formData = await this.apiService.get('/register/form-data');
            
                if (formData.delivery.vehicle_types) {
                    this.populateDropdown('vehicle-type', formData.delivery.vehicle_types);
                }
                if (formData.delivery.service_areas) {
                    this.populateDropdown('service-area', formData.delivery.service_areas);
                }
                if (formData.delivery.delivery_companies) {
                    this.populateDropdown('delivery-company', formData.delivery.delivery_companies);
                }
        } catch (error) {
            console.error('Failed to load delivery dropdowns:', error);
        }
    }

    // NGO Registration
    async submitNgoForm(data) {
        try {
            const response = await this.apiService.post('/register/ngo', data);
            this.showSuccessMessage('toaster.success.ngo_registration');
            return response;
        } catch (error) {
            this.showErrorMessage('toaster.error.ngo_registration');
            throw error;
        }
    }

    async loadNgoDropdowns() {
        try {
            const formData = await this.apiService.get('/register/form-data');
            
                if (formData.ngo.categories) {
                    this.populateDropdown('ngo-category', formData.ngo.categories);
                }
                if (formData.ngo.focus_areas) {
                    this.populateDropdown('focus-area', formData.ngo.focus_areas);
                }
                if (formData.ngo.regions) {
                    this.populateDropdown('ngo-region', formData.ngo.regions);
                }
            
        } catch (error) {
            console.error('Failed to load NGO dropdowns:', error);
        }
    }

    // Helper method to populate dropdowns
    populateDropdown(selectId, data) {
        
        const select = document.getElementById(selectId);
        if (!select) {
            console.error(`Select element with id '${selectId}' not found`);
            return;
        }
        

        // Check if this select has a Choices instance
        if (select.choicesInstance) {
            // Work with Choices.js instance
            this.populateChoicesDropdown(select.choicesInstance, data, selectId);
        } else {
            // Fallback to original method for non-Choices selects
            this.populateOriginalDropdown(select, data, selectId);
        }
    }

    // Method to populate Choices.js dropdowns
    populateChoicesDropdown(choicesInstance, data, selectId) {
        
        // Clear existing choices
        choicesInstance.clearChoices();
        

        let choices = [];

        // Check if data is an object and handle different data structures
        if (typeof data === 'object' && !Array.isArray(data)) {
            // Handle object with key-value pairs (like services_offered)
            if (selectId === 'services-offered') {
                choices = Object.keys(data).map(key => ({
                    value: key,
                    label: data[key]
                }));
            }
            
            // Handle restaurant_types object
            if (selectId === 'type-of-restaurant') {
                choices = Object.keys(data).map(key => ({
                    value: data[key],
                    label: key
                }));
            }
        } else if (Array.isArray(data) && data.length > 0) {
            // Handle arrays with objects containing id and name (categories, zones)
            if (typeof data[0] === 'object') {
                choices = data.map(item => ({
                    value: item.id || item.value,
                    label: item.name || item.label
                }));
            } else {
                // Handle simple arrays
                choices = data.map(item => ({
                    value: item.id || item.value,
                    label: item.name || item.label
                }));
            }
        }

        // Add the choices to the Choices instance
        if (choices.length > 0) {
            choicesInstance.setChoices(choices, 'value', 'label', false);
        } else {
            console.warn('No choices to add');
        }
    }

    // Method to populate original select dropdowns (fallback)
    populateOriginalDropdown(select, data, selectId) {
        // Clear existing options
        select.innerHTML = '<option value="">Select an option</option>';
        
        // Check if data is an object and handle different data structures
        if (typeof data === 'object' && !Array.isArray(data)) {
            // Handle object with key-value pairs (like services_offered)
            if (selectId === 'services-offered') {
                Object.keys(data).forEach(key => {
                    const option = document.createElement('option');
                    option.value = key;
                    option.textContent = data[key];
                    select.appendChild(option);
                });
                return;
            }
            
            // Handle restaurant_types object
            if (selectId === 'type-of-restaurant') {
                Object.keys(data).forEach(key => {
                    const option = document.createElement('option');
                    option.value = data[key];
                    option.textContent = key;
                    select.appendChild(option);
                });
                return;
            }
        }
        
        // Handle arrays with objects containing id and name (categories, zones)
        if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'object') {
            data.forEach(item => {
                const option = document.createElement('option');
                option.value = item.id || item.value;
                option.textContent = item.name || item.label;
                select.appendChild(option);
            });
            return;
        }

        // Add new options
        data.forEach(item => {
            const option = document.createElement('option');
            option.value = item.id || item.value;
            option.textContent = item.name || item.label;
            select.appendChild(option);
        });
    }

    // Success message with toaster
    showSuccessMessage(message) {
        const translatedMessage = this.translateMessage(message);
        this.showToaster(translatedMessage, 'success');
    }

    // Error message with toaster
    showErrorMessage(message) {
        const translatedMessage = this.translateMessage(message);
        this.showToaster(translatedMessage, 'error');
    }

    // Translate message using language manager
    translateMessage(message) {
        // Check if language manager is available
        if (window.languageManager && typeof window.languageManager.getTranslation === 'function') {
            // Try to translate the message
            const translation = window.languageManager.getTranslation(message);
            return translation || message; // Return original message if no translation found
        }
        return message; // Return original message if language manager not available
    }

    // Update toaster position when language changes
    updateToasterPosition() {
        const toasterContainer = document.getElementById('toaster-container');
        if (toasterContainer) {
            const isRTL = document.documentElement.dir === 'rtl' || 
                         document.documentElement.getAttribute('lang') === 'ar';
            
            toasterContainer.className = `
                fixed top-5 z-50 flex flex-col gap-2.5 ${isRTL ? 'right-5 left-5 sm:right-auto' : 'left-5 right-5 sm:left-auto'}
            `;
        }
    }

    // Toaster notification system
    showToaster(message, type = 'info') {
        let toasterContainer = document.getElementById('toaster-container');
        const isRTL = document.documentElement.dir === 'rtl' || document.documentElement.getAttribute('lang') === 'ar';

        // Always update container position based on current language
        if (!toasterContainer) {
            toasterContainer = document.createElement('div');
            toasterContainer.id = 'toaster-container';
            document.body.appendChild(toasterContainer);
        }
        toasterContainer.className = `fixed top-5 z-50 flex flex-col gap-2.5  ${isRTL ? 'right-5 left-5 sm:right-auto' : 'left-5 right-5 sm:left-auto'}`;

        // Create toast element
        const toast = document.createElement('div');
        let bgColor, icon;
        switch (type) {
            case 'success':
                bgColor = 'bg-green-500';
                icon = '✓';
                break;
            case 'error':
                bgColor = 'bg-red-500';
                icon = '✕';
                break;
            case 'warning':
                bgColor = 'bg-yellow-500';
                icon = '⚠';
                break;
            default:
                bgColor = 'bg-blue-500';
                icon = 'ℹ';
        }
        toast.className = `
            min-w-64 p-4 rounded-lg text-white font-medium shadow-lg
            flex items-center justify-between cursor-pointer
            transform transition-transform duration-300 ease-in-out
            ${bgColor}
            ${isRTL ? '-translate-x-full' : 'translate-x-full'}
        `;


        // Create message content
        const messageDiv = document.createElement('div');
        messageDiv.className = 'flex-1 text-sm leading-relaxed';
        messageDiv.textContent = message;

        // Create close button (only one!)
        const closeBtn = document.createElement('button');
        closeBtn.innerHTML = '×';
        closeBtn.className = `
            bg-transparent border-none text-white text-xl font-bold
            cursor-pointer p-0 w-5 h-5 flex items-center justify-center
            opacity-80 hover:opacity-100 transition-opacity duration-200
            ${isRTL ? 'mr-3' : 'ml-3'}
        `;

        // Append elements to toast (icon, message, close button)
        toast.appendChild(messageDiv);
        toast.appendChild(closeBtn);

        // Add toast to container
        toasterContainer.appendChild(toast);

        // Animate in
        setTimeout(() => {
            toast.classList.remove(isRTL ? '-translate-x-full' : 'translate-x-full');
            toast.classList.add('translate-x-0');
        }, 10);

        // Auto remove after 5 seconds
        const autoRemove = setTimeout(() => {
            this.removeToast(toast);
        }, 5000);

        // Close button and toast click functionality (only one close button)
        const closeToast = () => {
            clearTimeout(autoRemove);
            this.removeToast(toast);
        };
        closeBtn.addEventListener('click', closeToast);
        toast.addEventListener('click', closeToast);
    }

    // Remove toast with animation
    removeToast(toast) {
        // Check if page is RTL
        const isRTL = document.documentElement.dir === 'rtl' || 
                     document.documentElement.getAttribute('lang') === 'ar';
        
        // Remove translate-x-0 and add slide-out animation
        toast.classList.remove('translate-x-0');
        toast.classList.add(isRTL ? '-translate-x-full' : 'translate-x-full');
        
        setTimeout(() => {
            if (toast.parentNode) {
                toast.parentNode.removeChild(toast);
            }
        }, 300);
    }
}

// Export the registration API
window.RegistrationAPI = RegistrationAPI; 